import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./QuotationDetailPage.css";

type QuotationStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "approved"
  | "rejected"
  | "expired"
  | "cancelled";

type QuotationHeader = {
  id: string;
  organization_id: string;
  customer_id: string | null;
  quotation_number: string;
  status: QuotationStatus;
  issue_date: string;
  valid_until: string | null;
  currency_code: string;
  subtotal: number | string;
  discount_total: number | string;
  tax_total: number | string;
  total: number | string;
  notes: string | null;
  terms: string | null;
  created_at: string;
  updated_at: string;
};

type Customer = {
  id: string;
  name: string;
  document_type: string | null;
  document_number: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  address: string | null;
  city: string | null;
  status: string;
};

type QuotationItem = {
  id: string;
  catalog_item_id: string | null;
  sku: string | null;
  catalog_name: string | null;
  item_type: string | null;
  description: string;
  quantity: number | string;
  unit_price: number | string;
  discount_amount: number | string;
  tax_rate: number | string;
  line_total: number | string;
  sort_order: number;
};

type HistoryRow = {
  id: string;
  previous_status: QuotationStatus | null;
  new_status: QuotationStatus;
  note: string | null;
  changed_by: string | null;
  created_at: string;
};

type CustomerAsset = {
  id: string;
  asset_type: string;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  identifier: string | null;
  color: string | null;
  year: number | null;
  description: string | null;
  status: string;
};

type ServiceOrderLink = {
  id: string;
  order_number: string;
  status: string;
  priority: string;
  asset_id: string | null;
  received_at: string;
  updated_at: string;
};

type RelatedCommercialDocument = {
  relation_type: string;
  entity_type: string;
  entity_id: string;
  reference: string;
  status: string;
  issue_date: string | null;
  total: number | string | null;
  currency_code: string | null;
};

type QuotationDetail = {
  quotation: QuotationHeader;
  customer: Customer | null;
  items: QuotationItem[];
  history: HistoryRow[];
  assets: CustomerAsset[];
  service_order: ServiceOrderLink | null;
};

const STATUS_LABELS: Record<QuotationStatus, string> = {
  draft: "Borrador",
  sent: "Enviada",
  viewed: "Vista",
  approved: "Aprobada",
  rejected: "Rechazada",
  expired: "Vencida",
  cancelled: "Cancelada",
};

function relatedStatusLabel(value: string) {
  const labels: Record<string, string> = {
    draft: "Borrador",
    sent: "Enviada",
    viewed: "Vista",
    approved: "Aprobada",
    rejected: "Rechazada",
    expired: "Vencida",
    cancelled: "Cancelada",
    confirmed: "Confirmada",
    partially_paid: "Pago parcial",
    paid: "Pagada",
    issued: "Emitido",
    pending: "Pendiente",
    accepted: "Aceptado",
    received: "Recibido",
    processing: "Procesando",
    ready: "Listo",
    error: "Error",
  };
  return labels[value.toLowerCase()] ?? value;
}

function serviceStatusLabel(value: string) {
  const labels: Record<string, string> = {
    received: "Recibida",
    inspection: "Inspección",
    diagnosing: "Diagnóstico",
    awaiting_approval: "Esperando aprobación",
    approved: "Aprobada",
    in_progress: "En servicio",
    testing: "Pruebas",
    ready: "Terminada",
    delivered: "Entregada",
    cancelled: "Cancelada",
  };
  return labels[value.toLowerCase()] ?? value;
}

function priorityLabel(value: string) {
  const labels: Record<string, string> = {
    low: "Baja",
    normal: "Normal",
    high: "Alta",
    urgent: "Urgente",
  };
  return labels[value.toLowerCase()] ?? value;
}

function itemTypeLabel(value: string | null) {
  if (!value) return "";
  const labels: Record<string, string> = {
    product: "Producto",
    service: "Servicio",
    item: "Ítem",
  };
  return labels[value.toLowerCase()] ?? value;
}

function money(value: number | string | null, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function statusActions(status: QuotationStatus) {
  switch (status) {
    case "draft":
      return [
        { value: "sent" as const, label: "Marcar enviada" },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "sent":
      return [
        { value: "viewed" as const, label: "Marcar vista" },
        { value: "approved" as const, label: "Aprobar" },
        { value: "rejected" as const, label: "Rechazar", danger: true },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "viewed":
      return [
        { value: "approved" as const, label: "Aprobar" },
        { value: "rejected" as const, label: "Rechazar", danger: true },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    default:
      return [];
  }
}

function assetLabel(asset: CustomerAsset) {
  const identity = [asset.brand, asset.model].filter(Boolean).join(" ");
  return (
    identity ||
    asset.identifier ||
    asset.serial_number ||
    asset.description ||
    asset.asset_type
  );
}

export function QuotationDetailPage() {
  const { quotationId } = useParams();
  const { selectedOrganization } = useOrganization();

  const [detail, setDetail] = useState<QuotationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyStatus, setBusyStatus] = useState(false);
  const [converting, setConverting] = useState(false);
  const [assetId, setAssetId] = useState("");
  const [priority, setPriority] = useState("normal");
  const [customerReport, setCustomerReport] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [relatedDocuments, setRelatedDocuments] = useState<RelatedCommercialDocument[]>([]);
  const [creatingSale, setCreatingSale] = useState(false);

  const load = useCallback(async () => {
    if (!quotationId || !selectedOrganization) return;

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "get_quotation_detail",
      {
        p_quotation_id: quotationId,
      },
    );

    setLoading(false);

    if (rpcError) {
      setDetail(null);
      setError(rpcError.message);
      return;
    }

    const nextDetail = data as QuotationDetail;

    if (
      nextDetail.quotation.organization_id !==
      selectedOrganization.organizationId
    ) {
      setDetail(null);
      setError("La cotización pertenece a otra empresa.");
      return;
    }

    setDetail(nextDetail);

    const { data: relatedData } = await supabase.rpc(
      "list_related_commercial_documents",
      {
        p_organization_id: selectedOrganization.organizationId,
        p_quotation_id: nextDetail.quotation.id,
        p_sale_id: null,
        p_purchase_order_id: null,
      },
    );
    setRelatedDocuments((relatedData ?? []) as RelatedCommercialDocument[]);

    setAssetId((current) => {
      if (
        current &&
        nextDetail.assets.some((asset) => asset.id === current)
      ) {
        return current;
      }
      return nextDetail.assets[0]?.id ?? "";
    });
  }, [quotationId, selectedOrganization]);

  useEffect(() => {
    setDetail(null);
    setAssetId("");
    setPriority("normal");
    setCustomerReport("");
    setError(null);
    setSuccess(null);
    void load();
  }, [load]);

  async function changeStatus(nextStatus: QuotationStatus) {
    if (!detail) return;

    setBusyStatus(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "transition_quotation_status",
      {
        p_quotation_id: detail.quotation.id,
        p_new_status: nextStatus,
      },
    );

    setBusyStatus(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess(`Estado actualizado a ${STATUS_LABELS[nextStatus]}.`);
    await load();
  }

  async function createLinkedSale() {
    if (!detail || detail.quotation.status !== "approved") return;

    setCreatingSale(true);
    setError(null);
    setSuccess(null);

    const { data, error: rpcError } = await supabase.rpc(
      "create_sale_from_approved_quotation",
      { p_quotation_id: detail.quotation.id },
    );

    setCreatingSale(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess(data ? "Venta asociada correctamente." : "Venta asociada.");
    await load();
  }

  async function createServiceOrder() {
    if (!detail || detail.quotation.status !== "approved") return;

    setConverting(true);
    setError(null);
    setSuccess(null);

    const { data, error: rpcError } = await supabase.rpc(
      "create_service_order_from_quotation",
      {
        p_quotation_id: detail.quotation.id,
        p_asset_id: assetId || null,
        p_priority: priority,
        p_customer_report: customerReport.trim() || null,
      },
    );

    setConverting(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess(
      data
        ? "Orden de taller creada correctamente."
        : "La orden de taller quedó vinculada.",
    );
    await load();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Cargando cotización…</strong>
        </section>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="page">
        <div className="inline-alert" role="alert">
          {error ?? "Cotización no disponible."}
        </div>
        <Link className="compact-button quotation-detail-back" to="/cotizaciones">
          Volver a cotizaciones
        </Link>
      </div>
    );
  }

  const { quotation, customer, items, history, assets, service_order } = detail;
  const actions = statusActions(quotation.status);

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Cotización</span>
          <h1>{quotation.quotation_number}</h1>
          <p>{customer?.name ?? "Cliente no disponible"}</p>
        </div>

        <Link className="ghost-button quotation-detail-back" to="/cotizaciones">
          Volver
        </Link>
      </header>

      {error ? (
        <div className="inline-alert" role="alert">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="form-success quotation-detail-message">
          {success}
        </div>
      ) : null}

      <section className="quotation-detail-summary">
        <div className="metric-card">
          <span>Estado</span>
          <strong className="quotation-detail-state">
            {STATUS_LABELS[quotation.status]}
          </strong>
        </div>
        <div className="metric-card">
          <span>Total</span>
          <strong>{money(quotation.total, quotation.currency_code)}</strong>
        </div>
        <div className="metric-card">
          <span>Ítems</span>
          <strong>{items.length}</strong>
        </div>
        <div className="metric-card">
          <span>Válida hasta</span>
          <strong className="quotation-detail-date">
            {quotation.valid_until ?? "—"}
          </strong>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Trazabilidad comercial</span>
            <h2>Documentos relacionados</h2>
          </div>
          {quotation.status === "approved" &&
          !relatedDocuments.some((item) => item.entity_type === "sale") ? (
            <button
              className="primary-button"
              type="button"
              disabled={creatingSale}
              onClick={() => void createLinkedSale()}
            >
              {creatingSale ? "Creando venta…" : "Crear venta asociada"}
            </button>
          ) : null}
        </div>

        {relatedDocuments.length === 0 ? (
          <p className="settings-empty">
            Aún no hay documentos comerciales vinculados a esta cotización.
          </p>
        ) : (
          <div className="quotation-related-grid">
            {relatedDocuments.map((item) => {
              const to =
                item.entity_type === "sale"
                  ? `/ventas/${item.entity_id}`
                  : item.entity_type === "quotation"
                    ? `/cotizaciones/${item.entity_id}`
                    : "/contabilidad";

              const label =
                item.relation_type === "venta"
                  ? "Venta"
                  : item.relation_type === "comprobante_venta"
                    ? "Comprobante de venta"
                    : item.relation_type === "nota_credito"
                      ? "Nota de crédito"
                      : item.relation_type === "nota_debito"
                        ? "Nota de débito"
                        : "Cotización";

              return (
                <article className="quotation-related-card" key={item.entity_type + item.entity_id}>
                  <span className="status-badge">{label}</span>
                  <strong>{item.reference}</strong>
                  <small>{relatedStatusLabel(item.status)}</small>
                  <Link className="compact-button" to={to}>
                    Ver detalle
                  </Link>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {actions.length > 0 ? (
        <section className="panel-card quotation-detail-actions-panel">
          <div>
            <span className="eyebrow">Flujo</span>
            <h2>Estado de la cotización</h2>
          </div>
          <div className="quotation-detail-actions">
            {actions.map((action) => (
              <button
                key={action.value}
                type="button"
                className={
                  action.danger
                    ? "compact-button compact-button-danger"
                    : "compact-button"
                }
                disabled={busyStatus}
                onClick={() => void changeStatus(action.value)}
              >
                {busyStatus ? "Actualizando…" : action.label}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <section className="quotation-detail-grid">
        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Cliente</span>
              <h2>{customer?.name ?? "Sin cliente"}</h2>
            </div>
            {customer ? (
              <Link
                className="compact-button"
                to={`/clientes/${customer.id}`}
              >
                Abrir cliente
              </Link>
            ) : null}
          </div>

          <dl className="quotation-detail-meta">
            <div>
              <dt>Documento</dt>
              <dd>
                {customer?.document_number
                  ? `${customer.document_type ?? ""} ${customer.document_number}`.trim()
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Contacto</dt>
              <dd>{customer?.contact_name ?? "—"}</dd>
            </div>
            <div>
              <dt>Correo</dt>
              <dd>{customer?.email ?? "—"}</dd>
            </div>
            <div>
              <dt>Teléfono</dt>
              <dd>{customer?.phone ?? customer?.whatsapp ?? "—"}</dd>
            </div>
          </dl>
        </article>

        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Condiciones</span>
              <h2>Información comercial</h2>
            </div>
          </div>

          <dl className="quotation-detail-meta">
            <div>
              <dt>Emisión</dt>
              <dd>{quotation.issue_date}</dd>
            </div>
            <div>
              <dt>Moneda</dt>
              <dd>{quotation.currency_code}</dd>
            </div>
            <div>
              <dt>Notas</dt>
              <dd>{quotation.notes ?? "—"}</dd>
            </div>
            <div>
              <dt>Términos</dt>
              <dd>{quotation.terms ?? "—"}</dd>
            </div>
          </dl>
        </article>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Detalle</span>
            <h2>Ítems cotizados</h2>
          </div>
        </div>

        <div className="quotation-detail-items">
          {items.map((item) => (
            <article className="quotation-detail-item" key={item.id}>
              <div className="quotation-detail-item-main">
                <strong>{item.description}</strong>
                <span>
                  {item.sku ? `${item.sku} · ` : ""}
                  {item.catalog_name ?? "Ítem manual"}
                  {item.item_type ? ` · ${itemTypeLabel(item.item_type)}` : ""}
                </span>
              </div>

              <dl>
                <div>
                  <dt>Cantidad</dt>
                  <dd>{Number(item.quantity)}</dd>
                </div>
                <div>
                  <dt>Precio</dt>
                  <dd>{money(item.unit_price, quotation.currency_code)}</dd>
                </div>
                <div>
                  <dt>Descuento</dt>
                  <dd>{money(item.discount_amount, quotation.currency_code)}</dd>
                </div>
                <div>
                  <dt>IGV</dt>
                  <dd>{Number(item.tax_rate)}%</dd>
                </div>
              </dl>

              <strong className="quotation-detail-line-total">
                {money(item.line_total, quotation.currency_code)}
              </strong>
            </article>
          ))}
        </div>

        <div className="quotation-detail-totals">
          <div>
            <span>Base</span>
            <strong>{money(quotation.subtotal, quotation.currency_code)}</strong>
          </div>
          <div>
            <span>Descuento</span>
            <strong>
              {money(quotation.discount_total, quotation.currency_code)}
            </strong>
          </div>
          <div>
            <span>IGV</span>
            <strong>{money(quotation.tax_total, quotation.currency_code)}</strong>
          </div>
          <div className="quotation-detail-grand-total">
            <span>Total</span>
            <strong>{money(quotation.total, quotation.currency_code)}</strong>
          </div>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Taller</span>
            <h2>Orden de servicio</h2>
          </div>
        </div>

        {service_order ? (
          <div className="quotation-service-linked">
            <div>
              <span className="status-badge">{serviceStatusLabel(service_order.status)}</span>
              <strong>{service_order.order_number}</strong>
              <small>Prioridad {priorityLabel(service_order.priority)}</small>
            </div>
            <Link
              className="primary-button quotation-service-link"
              to={`/taller/${service_order.id}`}
            >
              Abrir orden
            </Link>
          </div>
        ) : quotation.status === "approved" ? (
          <div className="quotation-service-form">
            <label>
              Activo del cliente
              <select
                value={assetId}
                onChange={(event) => setAssetId(event.target.value)}
              >
                <option value="">Sin activo vinculado</option>
                {assets.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {assetLabel(asset)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Prioridad
              <select
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
              >
                <option value="low">Baja</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </label>

            <label className="quotation-service-report">
              Reporte de la familia / cliente
              <textarea
                rows={3}
                value={customerReport}
                onChange={(event) => setCustomerReport(event.target.value)}
                placeholder="Motivo de ingreso u observación inicial"
              />
            </label>

            <button
              className="primary-button"
              type="button"
              disabled={converting}
              onClick={() => void createServiceOrder()}
            >
              {converting ? "Creando orden…" : "Convertir a orden de taller"}
            </button>
          </div>
        ) : (
          <p className="settings-empty">
            La cotización debe estar aprobada antes de crear una orden de
            taller.
          </p>
        )}
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Historial</span>
            <h2>Cambios de estado</h2>
          </div>
        </div>

        {history.length === 0 ? (
          <p className="settings-empty">No hay cambios registrados.</p>
        ) : (
          <div className="quotation-history">
            {[...history].reverse().map((entry) => (
              <article key={entry.id}>
                <div>
                  <strong>{STATUS_LABELS[entry.new_status]}</strong>
                  <span>
                    {entry.previous_status
                      ? `${STATUS_LABELS[entry.previous_status]} → ${STATUS_LABELS[entry.new_status]}`
                      : "Estado inicial"}
                  </span>
                </div>
                <div>
                  <time>
                    {new Date(entry.created_at).toLocaleString("es-PE")}
                  </time>
                  {entry.note ? <small>{entry.note}</small> : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
